import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { DataElementView, DataSource, SourceFieldMapping } from "@/entities/all";
import { Skeleton } from "@/components/ui/skeleton";
import { Blocks, FileDown } from "lucide-react";
import { createPageUrl } from "@/utils";
import { FilterSelect, FilterSelectItem } from "@/components/ui/filter-select";

const STATUS_CONFIG = {
  DRAFT:          { bg: 'rgba(127,199,249,0.20)', color: 'rgba(55,122,214,0.70)',  label: 'Draft' },
  APPROVED:       { bg: 'rgba(77,179,0,0.15)',    color: 'rgba(77,179,0,0.90)',    label: 'Approved' },
  PENDING_REVIEW: { bg: 'rgba(233,171,48,0.20)',  color: 'rgba(233,171,48,0.90)', label: 'Pending Review' },
  DEPRECATED:     { bg: 'rgba(222,111,107,0.20)', color: 'rgba(222,111,107,0.90)',label: 'Deprecated' },
};

function getDataTypePill(type) {
  const t = (type || '').toLowerCase();
  if (t === 'string' || t === 'large_string')
    return { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)',   label: 'String' };
  if (t.startsWith('int') || t.startsWith('uint'))
    return { bg: 'rgba(167,139,250,0.20)', color: 'rgba(109,40,217,0.85)', label: type };
  if (t.startsWith('float') || t === 'decimal128')
    return { bg: 'rgba(99,102,241,0.15)',  color: 'rgba(79,70,229,0.90)',  label: type };
  if (t === 'bool')
    return { bg: 'rgba(77,179,0,0.15)',    color: 'rgba(77,179,0,0.90)',   label: 'Boolean' };
  if (t.includes('date') || t.includes('time') || t === 'timestamp' || t === 'duration')
    return { bg: 'rgba(251,146,60,0.20)',  color: 'rgba(234,88,12,0.85)', label: type };
  if (t === 'uuid')
    return { bg: 'rgba(6,182,212,0.15)',   color: 'rgba(6,182,212,0.90)', label: 'UUID' };
  if (t === 'binary' || t === 'large_binary')
    return { bg: 'rgba(148,163,184,0.20)', color: 'rgba(100,116,139,0.90)',label: 'Binary' };
  return { bg: 'rgba(162,178,201,0.20)', color: 'rgba(150,169,183,1)', label: type || '—' };
}

function Pill({ bg, color, label }) {
  return (
    <span className="inline-flex items-center gap-1 h-[18px] rounded-[9px] pl-1 pr-2" style={{ backgroundColor: bg }}>
      <span className="size-[10px] rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="text-xs font-medium whitespace-nowrap leading-none" style={{ color }}>{label}</span>
    </span>
  );
}

export default function DataDictionary() {
  const navigate = useNavigate();
  const [elements, setElements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dataSourceFilter, setDataSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dataSources, setDataSources] = useState([]);
  const [mappingsByElement, setMappingsByElement] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  // Live refresh: re-fetch on cortex:data-changed (broadcast by the Guided demo)
  // so elements/mappings appear as the assistant creates them.
  useEffect(() => {
    const onChanged = () => loadData({ quiet: true });
    window.addEventListener("cortex:data-changed", onChanged);
    return () => window.removeEventListener("cortex:data-changed", onChanged);
  }, []);

  // `quiet` = background refresh (live updates during a guided run): fetch + swap
  // data in WITHOUT flipping the full-page loading state, so the page doesn't blink
  // every few seconds. Only the initial load shows the spinner.
  const loadData = async ({ quiet = false } = {}) => {
    if (!quiet) setIsLoading(true);
    try {
      const [elementsData, sourcesData, mappingsData] = await Promise.all([
        DataElementView.list('canonical_name'),
        DataSource.list('source_name'),
        SourceFieldMapping.list(),
      ]);
      setElements(elementsData);
      setDataSources(sourcesData);

      // Build mapping: element_id -> [{ source_id, source_name }]
      const sourceMap = {};
      sourcesData.forEach(s => { sourceMap[s.id] = s.source_name; });

      const byElement = {};
      mappingsData.forEach(m => {
        if (!m.canonical_element_id) return;
        if (!byElement[m.canonical_element_id]) byElement[m.canonical_element_id] = [];
        byElement[m.canonical_element_id].push({
          source_id: m.source_id,
          source_name: sourceMap[m.source_id] || `Source ${m.source_id}`,
        });
      });
      setMappingsByElement(byElement);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      if (!quiet) setIsLoading(false);
    }
  };

  // Filter elements
  const filteredElements = useMemo(() => {
    return elements.filter(element => {
      const matchesSearch = searchTerm === "" ||
        element.canonical_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        element.definition?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        false;

      const matchesDataSource = dataSourceFilter === "all" ||
        (mappingsByElement[element.id] || []).some(m => m.source_name === dataSourceFilter);
      const matchesStatus = statusFilter === "all" || element.status === statusFilter;

      return matchesSearch && matchesDataSource && matchesStatus;
    });
  }, [elements, searchTerm, dataSourceFilter, statusFilter, mappingsByElement]);


  const hasActiveFilters = searchTerm || dataSourceFilter !== "all" || statusFilter !== "all";

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - margin * 2;

    // === FIRST PASS: Calculate TOC height ===
    let tocHeight = 0;
    tocHeight += 8; // TOC header
    tocHeight += filteredElements.length * 4;

    const headerHeight = 30;
    const availableFirstPage = pageHeight - margin * 2 - headerHeight;
    let tocPagesNeeded = 1;
    if (tocHeight > availableFirstPage) {
      tocPagesNeeded = 1 + Math.ceil((tocHeight - availableFirstPage) / (pageHeight - margin * 2));
    }

    for (let i = 1; i < tocPagesNeeded; i++) {
      doc.addPage();
    }
    doc.addPage();

    const elementAnchors = {};
    let yPos = margin;

    const addNewPageIfNeeded = (requiredHeight) => {
      if (yPos + requiredHeight > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // === SECOND PASS: Render elements ===
    for (const element of filteredElements) {
      const definitionLines = element.definition ? doc.splitTextToSize(element.definition, contentWidth - 10) : [];
      const estimatedHeight = 25 + (definitionLines.length * 4);

      addNewPageIfNeeded(estimatedHeight);
      elementAnchors[element.canonical_name] = { page: doc.internal.getNumberOfPages(), y: yPos };

      doc.setFontSize(11);
      doc.setTextColor(30, 41, 59);
      doc.setFont(undefined, 'bold');
      doc.text(element.canonical_name, margin, yPos);
      doc.setFont(undefined, 'normal');
      yPos += 5;

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      const metaText = `${element.data_type || 'N/A'} | ${(element.status || '').replace('_', ' ')}`;
      doc.text(metaText, margin, yPos);
      yPos += 6;

      if (element.definition) {
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.setFont(undefined, 'bold');
        doc.text("DEFINITION", margin, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 4;

        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        for (const line of definitionLines) {
          addNewPageIfNeeded(5);
          doc.text(line, margin, yPos);
          yPos += 4;
        }
        yPos += 2;
      }

      if (!element.definition) {
        doc.setFontSize(9);
        doc.setTextColor(148, 163, 184);
        doc.setFont(undefined, 'italic');
        doc.text("No documentation available", margin, yPos);
        doc.setFont(undefined, 'normal');
        yPos += 4;
      }

      yPos += 8;
    }

    // === THIRD PASS: Go back to page 1 and render TOC ===
    doc.setPage(1);
    yPos = margin;

    doc.setFontSize(20);
    doc.setTextColor(30, 64, 175);
    doc.text("Mindshine Data Governance - Data Dictionary", margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    const statsText = `${filteredElements.length} elements${hasActiveFilters ? ' (filtered)' : ''} | Generated: ${new Date().toLocaleDateString()}`;
    doc.text(statsText, margin, yPos);
    yPos += 10;

    doc.setFontSize(14);
    doc.setTextColor(30, 64, 175);
    doc.text("Table of Contents", margin, yPos);
    yPos += 8;

    let currentTocPage = 1;

    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    for (const element of filteredElements) {
      if (yPos > pageHeight - margin - 5) {
        currentTocPage++;
        doc.setPage(currentTocPage);
        yPos = margin;
      }

      const elAnchor = elementAnchors[element.canonical_name];
      doc.textWithLink(`• ${element.canonical_name}`, margin, yPos, { pageNumber: elAnchor.page, top: elAnchor.y });
      doc.setTextColor(100, 116, 139);
      doc.text(`${elAnchor.page}`, pageWidth - margin - 10, yPos);
      doc.setTextColor(71, 85, 105);
      yPos += 4;
    }

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    doc.save(`data-dictionary-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
      {/* Title */}
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[32px] font-medium leading-tight text-page-title">Data Dictionary</h1>
        <p className="text-sm font-normal text-page-muted">
          Comprehensive documentation of all canonical data elements in the organization
        </p>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <path d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z" stroke="#377AD6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            placeholder="Search elements by name or definition..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-page-text placeholder:text-page-muted"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0 max-w-full">
          <FilterSelect value={dataSourceFilter} onValueChange={setDataSourceFilter} placeholder="Data Source">
            <FilterSelectItem value="all">All Sources</FilterSelectItem>
            {dataSources.map(ds => (
              <FilterSelectItem key={ds.id} value={ds.source_name}>{ds.source_name}</FilterSelectItem>
            ))}
          </FilterSelect>
          <FilterSelect value={statusFilter} onValueChange={setStatusFilter} placeholder="Status">
            <FilterSelectItem value="all">All Status</FilterSelectItem>
            <FilterSelectItem value="APPROVED">Approved</FilterSelectItem>
            <FilterSelectItem value="DRAFT">Draft</FilterSelectItem>
            <FilterSelectItem value="PENDING_REVIEW">Pending Review</FilterSelectItem>
            <FilterSelectItem value="DEPRECATED">Deprecated</FilterSelectItem>
          </FilterSelect>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Blocks size={24} color="#377AD6" />
          <span className="text-sm font-medium text-[#377AD6]">
            {filteredElements.length} element{filteredElements.length !== 1 ? 's' : ''}
            {hasActiveFilters && ` (filtered from ${elements.length})`}
          </span>
        </div>
        <button
          onClick={exportToPDF}
          disabled={filteredElements.length === 0}
          className="flex items-center gap-2 w-[183px] h-9 rounded-[5px] bg-nav-active text-white text-sm font-medium pl-4 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed shrink-0 hover:bg-nav-active-dark transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <path d="M14 2.26946V6.4C14 6.96005 14 7.24008 14.109 7.45399C14.2049 7.64215 14.3578 7.79513 14.546 7.89101C14.7599 8 15.0399 8 15.6 8H19.7305M20 9.98822V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V6.8C4 5.11984 4 4.27976 4.32698 3.63803C4.6146 3.07354 5.07354 2.6146 5.63803 2.32698C6.27976 2 7.11984 2 8.8 2H12.0118C12.7455 2 13.1124 2 13.4577 2.08289C13.7638 2.15638 14.0564 2.27759 14.3249 2.44208C14.6276 2.6276 14.887 2.88703 15.4059 3.40589L18.5941 6.59411C19.113 7.11297 19.3724 7.3724 19.5579 7.67515C19.7224 7.94356 19.8436 8.2362 19.9171 8.5423C20 8.88757 20 9.25445 20 9.98822Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Export PDF
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[90px] w-full rounded-[5px]" />
          ))}
        </div>
      ) : filteredElements.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <Blocks size={48} className="text-page-muted opacity-40" />
          <p className="text-sm text-page-muted">No elements found matching your criteria</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredElements.map((element) => {
            const statusPill = element.status ? STATUS_CONFIG[element.status.toUpperCase()] : null;
            const typePill = element.data_type ? getDataTypePill(element.data_type) : null;
            return (
              <div
                key={element.id}
                className="group relative bg-card-bg rounded-[5px] px-5 py-3 cursor-pointer transition-shadow hover:shadow-[0_14px_42px_0_var(--shadow-blue-lg)] hover:z-10"
                onClick={() => navigate(createPageUrl(`element-details?id=${element.id}`))}
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[22px] font-medium leading-tight text-page-title group-hover:text-nav-active transition-colors">
                      {element.canonical_name}
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
                      <path d="M9 18L15 12L9 6" stroke="#377AD6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  {(statusPill || typePill) && (
                    <div className="flex items-center gap-2">
                      {statusPill && <Pill {...statusPill} />}
                      {typePill && <Pill {...typePill} />}
                    </div>
                  )}
                  <p className="text-sm text-page-muted">
                    {element.definition || 'No documentation available'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
